import Popover from '@mui/material/Popover';
import { Editor } from '@tiptap/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { IntelligenceTier } from '@common/schemas/novel.schema';

import { AIInstructionDialog } from './AIInstructionDialog';
import { AIPresetActionsList } from './AIPresetActionsList';
import {
  AnchorCoords,
  EditorWithRightClick,
  findScrollParent,
  formatTextToHtml,
  getSelectionRect,
  POPOVER_PAPER_SX,
  POPOVER_ROOT_SX
} from './FloatingBubbleMenu.utils';
import { TextFormattingToolbar } from './TextFormattingToolbar';

interface FloatingBubbleMenuProps {
  editor: Editor;
  onAddComment?: () => void;
  intelligenceTier?: IntelligenceTier;
  novelId?: string;
}

const ACTIONS_REQUIRING_PROMPT = ['rewrite_selection', 'fix_pacing'] as const;
const CONTEXT_WINDOW = 2000;
const POLL_INTERVAL_MS = 150;

const preventContextMenu = (e: React.MouseEvent) => e.preventDefault();

export const FloatingBubbleMenu = memo(
  ({ editor, onAddComment, intelligenceTier, novelId }: FloatingBubbleMenuProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { hasFeature, isTokenDepleted } = useSubscription();
    const { backend } = useFirebase();

    const [isProcessing, setIsProcessing] = useState(false);
    const [actionProcessing, setActionProcessing] = useState<string | null>(null);
    const [modalAction, setModalAction] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [userInput, setUserInput] = useState('');

    const [open, setOpen] = useState(false);
    const [isSelectionEmpty, setIsSelectionEmpty] = useState(true);
    const [anchorPosition, setAnchorPosition] = useState<AnchorCoords | null>(null);

    // ── Selection tracking & scroll-spy ────────────────────────────

    const isMouseDownRef = useRef(false);

    useEffect(() => {
      if (!editor) return;

      const updateMenuState = () => {
        const { from, to } = editor.state.selection;
        const isEmpty = from === to;
        setIsSelectionEmpty(isEmpty);

        const extendedEditor = editor as EditorWithRightClick;
        const shouldShow =
          (!isEmpty && editor.isFocused && !isMouseDownRef.current) || !!extendedEditor.showOnRightClick;
        setOpen(shouldShow);

        if (shouldShow) {
          const rect = getSelectionRect(editor);
          setAnchorPosition({
            top: rect.top,
            left: rect.left + rect.width / 2,
          });
        } else {
          setAnchorPosition(null);
        }
      };

      const handleDown = () => { isMouseDownRef.current = true; };
      const handleUp = () => {
        isMouseDownRef.current = false;
        updateMenuState();
      };

      window.addEventListener('mousedown', handleDown);
      window.addEventListener('mouseup', handleUp);

      editor.on('selectionUpdate', updateMenuState);
      editor.on('focus', updateMenuState);
      editor.on('blur', updateMenuState);

      const scrollParent = findScrollParent(editor.view.dom.parentElement);
      scrollParent?.addEventListener('scroll', updateMenuState, { passive: true });
      window.addEventListener('scroll', updateMenuState, { passive: true });

      const interval = setInterval(updateMenuState, POLL_INTERVAL_MS);

      return () => {
        window.removeEventListener('mousedown', handleDown);
        window.removeEventListener('mouseup', handleUp);
        editor.off('selectionUpdate', updateMenuState);
        editor.off('focus', updateMenuState);
        editor.off('blur', updateMenuState);
        scrollParent?.removeEventListener('scroll', updateMenuState);
        window.removeEventListener('scroll', updateMenuState);
        clearInterval(interval);
      };
    }, [editor]);

    // ── AI text processing ─────────────────────────────────────────

    const callProcessText = useCallback(
      async (action: string, customPrompt?: string) => {
        if (!backend || !editor) return;

        const { from, to } = editor.state.selection;
        const text = editor.state.doc.textBetween(from, to);
        if (!text && action !== 'continue_writing') return;

        const precedingContext = editor.state.doc.textBetween(
          Math.max(0, from - CONTEXT_WINDOW),
          from,
        );

        setIsProcessing(true);
        setActionProcessing(action);
        try {
          const data = await backend.ai.processText({
            action,
            text: text || '',
            prompt: customPrompt,
            context: precedingContext,
            tier: intelligenceTier,
            novelId,
          });

          if (action === 'continue_writing') {
            const formatted = data.result.includes('\n')
              ? formatTextToHtml(data.result)
              : ' ' + data.result;
            editor.chain().focus().insertContentAt(to, formatted).run();
          } else {
            const formatted = formatTextToHtml(data.result);
            editor
              .chain()
              .focus()
              .deleteRange({ from, to })
              .insertContentAt(from, formatted)
              .run();
          }
        } catch (error) {
          console.error('AI processing failed:', error);
          toast({
            title: t('common.error'),
            description: t('editor.failed_to_process'),
            variant: 'destructive',
          });
        } finally {
          setIsProcessing(false);
          setActionProcessing(null);
        }
      },
      [backend, editor, intelligenceTier, novelId, toast, t],
    );

    const handleAIAction = useCallback(
      async (action: string) => {
        if ((ACTIONS_REQUIRING_PROMPT as readonly string[]).includes(action)) {
          setModalAction(action);
          setUserInput('');
          setModalOpen(true);
        } else {
          await callProcessText(action);
        }
      },
      [callProcessText],
    );

    const handleModalSubmit = async () => {
      if (!modalAction) return;
      setModalOpen(false);
      await callProcessText(modalAction, userInput);
      setModalAction(null);
    };

    // ── Misc handlers ──────────────────────────────────────────────

    const handleCopyClick = useCallback(() => {
      if (!editor) return;
      const { from, to } = editor.state.selection;
      const text = editor.state.doc.textBetween(from, to);
      navigator.clipboard.writeText(text);
    }, [editor]);

    const handleClose = () => {
      setOpen(false);
      const extendedEditor = editor as EditorWithRightClick | null;
      if (extendedEditor) {
        extendedEditor.showOnRightClick = false;
      }
    };

    if (!editor) return null;

    // ── Render ─────────────────────────────────────────────────────

    return (
      <>
        <Popover
          open={open && !!anchorPosition}
          anchorReference="anchorPosition"
          anchorPosition={anchorPosition ?? undefined}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          disableAutoFocus
          disableEnforceFocus
          hideBackdrop
          slotProps={{
            root: { sx: POPOVER_ROOT_SX },
            paper: {
              elevation: 6,
              onContextMenu: preventContextMenu,
              sx: POPOVER_PAPER_SX,
            },
          }}
        >
          <TextFormattingToolbar editor={editor} />
          <AIPresetActionsList
            hasFeature={hasFeature}
            isTokenDepleted={isTokenDepleted}
            isProcessing={isProcessing}
            actionProcessing={actionProcessing}
            onActionClick={handleAIAction}
            onAddComment={onAddComment}
            onCopyClick={handleCopyClick}
            isSelectionEmpty={isSelectionEmpty}
          />
        </Popover>

        <AIInstructionDialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          action={modalAction}
          value={userInput}
          onChange={setUserInput}
          onSubmit={handleModalSubmit}
          isProcessing={isProcessing}
        />
      </>
    );
  },
);

FloatingBubbleMenu.displayName = 'FloatingBubbleMenu';
